import { useEffect, useState } from "react";
import { fetchAccountWallets, fetchCreditBalance, fetchCreditWallets, fetchNewWallets, fetchRepaymentWallet } from "api/credit-fetchers";
import { fetchCurrentBusiness, fetchTransferFees, handleFetchAllBanks, handleGetWalletAccounts, handleGetWallets } from "api/fetchers";
import { useQueryState } from "nuqs";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { unformat } from "@react-input/number-format";

const useUserDetails = ({ enableBankList = false, enableTransferFees = false, amount } = {}) => {
  const {
    users: user,
    business,
    currency,
    businessSwitching,
    wallets,
  } = useSelector((state) => state);
  const hasSelectedWallet = (wallets || []).some(
    (wallet) =>
      String(wallet?.currency || "").toUpperCase() ===
      String(currency || "").toUpperCase()
  );
  const [range, setRange] = useQueryState("range", {
    defaultValue: "all",
  });
  const isBusinessApproved = business?.status === "APPROVED";

  const {
    data: userWallets,
    isLoading: loadingWallets,
    refetch: refetchWallet,
  } = useQuery({
    queryKey: ["user-wallet", business?._id, currency],
    queryFn: ({ signal }) =>
      handleGetWallets({ businessId: business?._id, currency, signal }),
    enabled:
      !businessSwitching &&
      hasSelectedWallet &&
      isBusinessApproved &&
      Boolean(business?._id && currency),
  });

  const {
    data: userWalletsAccounts,
    isLoading: loadingWalletsAccounts,
    refetch: refetchWalletAccounts,
  } = useQuery({
    queryKey: ["user-wallet-accounts", business?._id],
    queryFn: () => handleGetWalletAccounts({ businessId: business?._id }),
    enabled: !businessSwitching && isBusinessApproved && Boolean(business?._id),
  });

  const { data: creditWalletData, isLoading: isLoadingCreditWallet } = useQuery({
    queryKey: ["fetchCreditWallets", business?._id, currency],
    queryFn: () => fetchCreditWallets({ currency, businessId: business?._id }),
    enabled:
      !businessSwitching &&
      hasSelectedWallet &&
      isBusinessApproved &&
      Boolean(business?._id && currency),
  });
  const {
    data: currentBusinessAccount,
    isLoading: isLoadingCurrentBusinessAccount,
    refetch: refetchCurrentBusinessAccount,
  } = useQuery({
    queryKey: ["fetchCurrentBusiness"],
    queryFn: () => fetchCurrentBusiness(),
  });

  const { data, isLoading: isLoadingCreditBalance } = useQuery({
    queryKey: ["fetchCreditBalance", business?._id, range, currency],
    queryFn: () => fetchCreditBalance({ currency, businessId: business?._id, range }),
    enabled:
      !businessSwitching &&
      hasSelectedWallet &&
      isBusinessApproved &&
      Boolean(business?._id && currency),
  });

  const { data: bankList } = useQuery({
    queryKey: ["bank-list", business?._id, currency],
    queryFn: () => handleFetchAllBanks(currency),
    enabled:
      enableBankList &&
      !businessSwitching &&
      hasSelectedWallet &&
      isBusinessApproved &&
      Boolean(business?._id && currency),
  });

  const {
    data: newWalletsData,
    isLoading: isLoadingNewWallets,
    refetch: refetchNewWallets,
  } = useQuery({
    queryKey: ["fetchNewWallets", business?._id],
    queryFn: () =>
      fetchNewWallets({
        businessId: business?._id,
        currency: currency,
      }),
    enabled: !businessSwitching && isBusinessApproved && Boolean(business?._id),
  });

  const {
    data: accountWalletsData,
    isLoading: isLoadingAccountWallets,
  } = useQuery({
    queryKey: ["accountWallets"],
    queryFn: () =>
      fetchAccountWallets({
        businessId: business?._id,
      }),
    enabled: !!business?._id && isBusinessApproved
  });

  const [debouncedAmount, setDebouncedAmount] = useState(amount);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAmount(amount);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [amount]);

  const {
    data: transferFeesData,
    isLoading: isLoadingTransferFees,
  } = useQuery({
    queryKey: ["fetchTransferFees", currency, business?._id, debouncedAmount],
    queryFn: () =>
      fetchTransferFees({
        businessId: business?._id,
        currency: currency,
        amount: unformat(debouncedAmount),
      }),
    enabled:
      enableTransferFees &&
      !businessSwitching &&
      hasSelectedWallet &&
      isBusinessApproved &&
      Boolean(business?._id && currency),
  });

  const { data: repaymentWalletDetails, isLoading: isLoadingRepaymentWallet } = useQuery({
    queryKey: [currency, business?._id, "getRepaymentWallet"],
    queryFn: () => fetchRepaymentWallet({ businessId: business?._id, currency }),
    retry: 3,
    enabled:
      !businessSwitching &&
      isBusinessApproved
  });


  const bankListOptions = bankList?.data?.map((option) => ({
    label: option?.name,
    value: option?.code,
  }));
  const creditBalanceData = data?.data;
  const creditWalletBalance = creditWalletData?.data?.balance;
  const creditWalletId = creditWalletData?.data?._id;
  const walletCurrency = creditWalletData?.data?.currency;

  const walletBalance = userWallets?.balance;
  const walletBallanceId = userWallets?._id;

  return {
    user,
    business,
    businessId: business?._id,
    currency,
    walletBalance,
    userWallets,
    loadingWallets,
    refetchWallet,
    isBusinessApproved: isBusinessApproved,
    creditBalanceData,
    walletCurrency,
    creditWalletBalance,
    isLoadingCreditBalance,
    isLoadingCreditWallet,
    creditWalletId,
    walletBallanceId,
    range,
    setRange,
    isLoadingCurrentBusinessAccount,
    currentBusinessAccount,
    hasPaidOpeningFee: currentBusinessAccount?.hasPaidOpeningFee,
    openingFeeAmount: currentBusinessAccount?.openingFeeAmount,
    openingFeeCurrency: currentBusinessAccount?.openingFeeCurrency,
    openingFeePaidAmount: currentBusinessAccount?.openingFeePaidAmount,
    openingFeePaidAt: currentBusinessAccount?.openingFeePaidAt,
    refetchCurrentBusinessAccount,
    bankListOptions,
    newWalletsData: newWalletsData?.data,
    accountWalletsData: accountWalletsData?.data,
    isLoadingAccountWallets,
    isLoadingNewWallets,
    refetchNewWallets,
    transferFeesData,
    isLoadingTransferFees,
    userWalletsAccounts,
    loadingWalletsAccounts,
    refetchWalletAccounts,
    repaymentWalletDetails,
    isLoadingRepaymentWallet
  };
};

export default useUserDetails;
